import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, ClipboardCheck } from "lucide-react";
import Nav from "@/components/Nav";
import ShareButton from "@/components/ShareButton";
import PageView, { CoverView } from "@/components/PageView";
import { api } from "@/lib/api";

export default function Preview() {
  const { bookId } = useParams();
  const [book, setBook] = useState(null);
  const [photos, setPhotos] = useState({});
  const [i, setI] = useState(0);
  useEffect(() => {
    api.get(`/books/${bookId}`).then((r) => setBook(r.data));
    api.get(`/books/${bookId}/photos`).then((r) => setPhotos(Object.fromEntries(r.data.map((p) => [p.id, p]))));
  }, [bookId]);
  if (!book) return null;
  const empty = book.pages.filter((p) => (p.photo_ids || []).length === 0 && p.layout !== "caption").length;
  const spreads = [[null, "cover"], ...Array.from({ length: Math.ceil(book.pages.length / 2) }, (_, k) => [book.pages[k * 2], book.pages[k * 2 + 1]])];
  const s = spreads[i];
  const props = { photos, size: book.size, theme: book.theme, enhance: book.enhance };
  const checks = [
    ["Every photo passed the 300 DPI print-quality check at upload", true],
    ["Bleed & safe margins applied for trimming", true],
    [empty ? `${empty} page(s) have empty photo slots` : "No empty photo slots", !empty],
    [`${book.page_count} pages · spine sized to page count`, true],
  ];
  return (
    <div className="min-h-screen bg-[#F5F0E8]">
      <Nav right={<Link to={`/books/${bookId}/edit`} data-testid="preview-back-to-editor" className="btn-ghost !py-2">Back to editor</Link>} />
      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <p className="overline">Print preview</p>
        <h1 className="mt-2 font-serif text-4xl tracking-tight">{book.title}</h1>
        <div className="mt-10 flex items-center justify-center gap-3 sm:gap-6">
          <button data-testid="preview-prev-btn" disabled={i === 0} onClick={() => setI(i - 1)} className="rounded-full bg-white p-3 shadow disabled:opacity-30"><ChevronLeft className="h-5 w-5" /></button>
          <div className="page-shadow flex w-full max-w-4xl bg-white" data-testid="preview-spread">
            {s[1] === "cover" ? (
              <><div className="hidden w-1/2 bg-[#1E1B24] sm:block" /><div className="w-full sm:w-1/2"><CoverView book={book} photos={photos} enhance={book.enhance} /></div></>
            ) : (
              <>
                <div className="w-1/2 border-r border-black/10">{s[0] && <PageView page={s[0]} {...props} />}</div>
                <div className="w-1/2">{s[1] ? <PageView page={s[1]} {...props} /> : <div className={`theme-${book.theme} h-full`} />}</div>
              </>
            )}
          </div>
          <button data-testid="preview-next-btn" disabled={i === spreads.length - 1} onClick={() => setI(i + 1)} className="rounded-full bg-white p-3 shadow disabled:opacity-30"><ChevronRight className="h-5 w-5" /></button>
        </div>
        <p className="mt-4 text-center font-mono text-xs text-[#8C8498]" data-testid="preview-spread-indicator">{i === 0 ? "Cover" : `Pages ${i * 2 - 1}–${i * 2}`} · {i + 1}/{spreads.length}</p>

        <div className="mx-auto mt-12 grid max-w-4xl gap-6 md:grid-cols-[1fr_auto] md:items-end">
          <div className="paper rounded-lg p-6" data-testid="preview-checklist">
            <p className="flex items-center gap-2 font-semibold"><ClipboardCheck className="h-5 w-5 text-[#789A82]" />Print check</p>
            <ul className="mt-4 space-y-2 text-sm">
              {checks.map(([t, ok]) => (
                <li key={t} className={`flex items-center gap-2 ${ok ? "text-[#5E5868]" : "text-[#D97706]"}`}><Check className="h-4 w-4" />{t}</li>
              ))}
            </ul>
          </div>
          <div className="flex flex-wrap gap-3">
            <ShareButton book={book} onChange={(t) => setBook({ ...book, share_token: t })} className="btn-ghost" />
            <Link to={`/books/${bookId}/checkout`} data-testid="preview-checkout-btn" className="btn-primary">Looks perfect — checkout</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
